// MOVE AN ACTIVITY AND EVERYTHING UNDER IT BY N WORKING DAYS.
//
// Owner, 2026-10-05: the tree offset by days in the schedule.
//
// THE PREVIEW IS THE FEATURE, not a courtesy. Shifting a branch by hand
// means editing every row, and the moment two rows move by different
// amounts the logic inside the branch is gone - a two-day lag becomes a
// five-day lag and nobody notices until the sequence is wrong on site. So
// this asks the server what it WOULD do, shows it, and only then offers to
// do it - and both come from the same computation server-side, so the rows
// named here are the rows that move.
//
// FOUR THINGS IT HAS TO SAY, because each one is a way this could be wrong
// in a way that looks like it worked:
//
//   WHAT MOVES, with before and after dates, so a reader can check the
//   arithmetic rather than trust a count;
//   WHAT IT IS LEAVING and why - a signed-off or already-started activity
//   keeps its dates, and a silently unmoved row is a schedule that reads as
//   shifted and is not;
//   WHICH LAGS CROSS THE EDGE of the move: a relationship whose other end
//   is not moving changes meaning, and that is the one thing a rigid shift
//   cannot protect;
//   AND WHETHER THE BRANCH SPANS TWO CALENDARS, which is refused until
//   somebody confirms, because N working days is a different number of
//   calendar days on each and the branch then does not move as one piece.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, InputNumber, Modal, Radio, Space, Table, Tag, Tooltip, message } from "antd";
import { offsetGanttSubtree } from "api/gantt/ganttApi";

const T = { muted: "#667085", line: "#EFEFEF" };

export default function ScheduleOffsetDialog({ projectId, row, onClose, onApplied }) {
  const [days, setDays] = useState(5);
  const [dir, setDir] = useState("later");
  const [state, setState] = useState({ loading: false, preview: null, error: "", mixedBlocked: false });
  const [saving, setSaving] = useState(false);

  const signed = dir === "earlier" ? -Math.abs(Number(days) || 0) : Math.abs(Number(days) || 0);

  const look = useCallback(async (allowMixed = false) => {
    if (!row || !row._id || !signed) { setState({ loading: false, preview: null, error: "", mixedBlocked: false }); return; }
    setState((s) => ({ ...s, loading: true, error: "", mixedBlocked: false }));
    try {
      const p = await offsetGanttSubtree(projectId, row._id, { days: signed, allowMixedCalendars: allowMixed });
      setState({ loading: false, preview: p, error: "", mixedBlocked: false });
    } catch (e) {
      // A 409 IS NOT AN ERROR TO SWALLOW: it is the mixed-calendar refusal,
      // and its body carries the whole preview - so the dialog shows what
      // WOULD happen and offers to go ahead, rather than a dead end.
      if (e && e.status === 409 && e.data) {
        setState({ loading: false, preview: e.data, error: e.message || "", mixedBlocked: true });
        return;
      }
      setState({ loading: false, preview: null, error: (e && e.message) || "Could not work that out", mixedBlocked: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, row, signed]);

  // Re-previewed as the number changes, debounced: the server computes it,
  // and a keystroke-per-request on a 300-row branch is a page that fights
  // the person typing.
  useEffect(() => {
    const t = setTimeout(() => look(false), 250);
    return () => clearTimeout(t);
  }, [look]);

  const p = state.preview;
  const apply = async () => {
    setSaving(true);
    try {
      const out = await offsetGanttSubtree(projectId, row._id, {
        days: signed, apply: true, allowMixedCalendars: state.mixedBlocked,
      });
      // THE NUMBER REPORTED IS WHAT THE WRITE DID, which the server counts
      // from the write rather than from what it asked for - a row deleted
      // by somebody else in the meantime is not a row that moved.
      message.success(`${out.moved} activit${out.moved === 1 ? "y" : "ies"} moved ${Math.abs(signed)} working day${Math.abs(signed) === 1 ? "" : "s"} ${signed > 0 ? "later" : "earlier"}`);
      if ((out.held || []).length) {
        message.info(`${out.held.length} left where ${out.held.length === 1 ? "it was" : "they were"} - already signed off or under way.`);
      }
      if (onApplied) onApplied(out);
      onClose();
    } catch (e) {
      message.error((e && e.message) || "Could not move that branch");
    } finally {
      setSaving(false);
    }
  };

  const cols = [
    { title: "Activity", key: "n", render: (_, m) => <span>{m.wbs ? <span style={{ color: T.muted }}>{m.wbs} </span> : null}{m.name}</span> },
    { title: "Start", key: "s", width: 190, render: (_, m) => (m.from.start ? <span><span style={{ color: T.muted }}>{m.from.start}</span> → <b>{m.to.start}</b></span> : <span style={{ color: T.muted }}>–</span>) },
    { title: "Finish", key: "e", width: 190, render: (_, m) => (m.from.end ? <span><span style={{ color: T.muted }}>{m.from.end}</span> → <b>{m.to.end}</b></span> : <span style={{ color: T.muted }}>–</span>) },
    {
      title: "", key: "f", width: 120,
      render: (_, m) => (m.fixedDeadline ? (
        <Tooltip title="This activity has a deadline somebody entered. It is a commitment to somebody else, so it stays where it is - only a deadline the forecast derived moves with the dates.">
          <Tag color="orange">deadline kept</Tag>
        </Tooltip>
      ) : null),
    },
  ];

  return (
    <Modal
      open={!!row}
      title={row ? `Move "${row.name}" and everything under it` : ""}
      onCancel={onClose}
      width={820}
      footer={[
        <Button key="c" onClick={onClose}>Cancel</Button>,
        <Button
          key="a" type="primary" danger={state.mixedBlocked}
          loading={saving}
          disabled={!p || !(p.moving || []).length || !signed}
          onClick={apply}
        >
          {state.mixedBlocked
            ? "Move it anyway"
            : `Move ${p ? (p.moving || []).length : 0} activit${p && (p.moving || []).length === 1 ? "y" : "ies"}`}
        </Button>,
      ]}
    >
      <Space align="center" wrap style={{ marginBottom: 10 }}>
        <span>Move by</span>
        <InputNumber min={1} max={365} value={days} onChange={(v) => setDays(v || 0)} style={{ width: 90 }} />
        <span>working days</span>
        <Radio.Group size="small" value={dir} onChange={(e) => setDir(e.target.value)}>
          <Radio.Button value="later">later</Radio.Button>
          <Radio.Button value="earlier">earlier</Radio.Button>
        </Radio.Group>
      </Space>
      <div style={{ color: T.muted, fontSize: 12.5, marginBottom: 10 }}>
        Working days on each activity&apos;s own calendar, so weekends and holidays are skipped rather than counted.
        Everything below this activity moves with it; nothing above it changes.
      </div>

      {state.error && !state.mixedBlocked ? <Alert type="error" showIcon message={state.error} /> : null}

      {/* THE MIXED-CALENDAR REFUSAL, in the server's own words, with the
          preview still shown - a refusal that hides what it refused is a
          dead end. */}
      {state.mixedBlocked ? (
        <Alert
          type="warning" showIcon style={{ marginBottom: 10 }}
          message="This branch spans more than one calendar"
          description={state.error}
        />
      ) : null}

      {p ? (
        <div>
          {(p.held || []).length ? (
            <Alert
              type="info" showIcon style={{ marginBottom: 10 }}
              message={`${p.held.length} activit${p.held.length === 1 ? "y is" : "ies are"} staying where ${p.held.length === 1 ? "it is" : "they are"}`}
              description={(
                <div style={{ maxHeight: 120, overflow: "auto" }}>
                  {p.held.map((h) => (
                    <div key={h._id}>{h.wbs ? `${h.wbs} ` : ""}{h.name} — <span style={{ color: T.muted }}>{h.why}</span></div>
                  ))}
                </div>
              )}
            />
          ) : null}

          {(p.crossing || []).length ? (
            <Alert
              type="warning" showIcon style={{ marginBottom: 10 }}
              message={`${p.crossing.length} relationship${p.crossing.length === 1 ? "" : "s"} cross${p.crossing.length === 1 ? "es" : ""} the edge of this move`}
              description={(
                <div style={{ maxHeight: 120, overflow: "auto" }}>
                  <div style={{ marginBottom: 4 }}>
                    Lags inside the branch are unaffected - both ends move together. These have one end outside it, so
                    the gap they describe will change:
                  </div>
                  {p.crossing.slice(0, 12).map((c, i) => (
                    <div key={`${c.predecessorId}-${i}`}>
                      {c.wbs ? `${c.wbs} ` : ""}{c.row} · {c.type}
                      {c.lagDays ? ` ${c.lagDays > 0 ? "+" : ""}${c.lagDays}d` : ""} · {c.direction}
                    </div>
                  ))}
                  {p.crossing.length > 12 ? <div style={{ color: T.muted }}>and {p.crossing.length - 12} more</div> : null}
                </div>
              )}
            />
          ) : null}

          <Table
            size="small" rowKey="_id" loading={state.loading}
            dataSource={p.moving || []} columns={cols}
            pagination={{ pageSize: 12, size: "small", hideOnSinglePage: true }}
            locale={{ emptyText: "Nothing here can move - every activity in this branch is signed off or already under way." }}
          />
          <div style={{ borderTop: `1px solid ${T.line}`, marginTop: 8, paddingTop: 8, color: T.muted, fontSize: 12.5 }}>
            {p.counted} in the branch · {(p.moving || []).length} moving · {(p.held || []).length} staying
            {(p.calendars || []).length ? ` · calendar${p.calendars.length === 1 ? "" : "s"}: ${p.calendars.join(", ")}` : ""}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
